/**
 * BulkSalesList.jsx
 * Full list/dashboard screen for Bulk Sales.
 * Includes KPI cards, rich filter panel, results table, notification feed.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaRocket, FaClock, FaShoppingCart, FaReceipt, FaCoins,
  FaSearch, FaRedo, FaEye, FaTrash, FaDownload, FaFileImport,
  FaChevronLeft, FaChevronRight, FaBell, FaFilter,
  FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaPlus,
  FaListAlt, FaChevronDown, FaChevronUp
} from "react-icons/fa";
import "./BulkSales.css";

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => `AED ${(parseFloat(n) || 0).toFixed(2)}`;

const today = () => new Date().toISOString().split("T")[0];

const monthRange = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return {
    from: `${y}-${m}-01`,
    to:   `${y}-${m}-${new Date(y, d.getMonth() + 1, 0).getDate()}`,
  };
};

// Mock notifications (will be replaced by API in production)
const MOCK_NOTIFICATIONS = [
  { id: 1, type: "warning", text: "Sale # BXO-B1-AUG26-984 seems to be incomplete.", time: "2 min ago" },
  { id: 2, type: "sale",    text: "Receipt # RV-B1-AUG26-029 seems to be incomplete.", time: "14 min ago" },
  { id: 3, type: "danger",  text: "Transaction (Sale# BXO-B1-AUG26-971) has been rejected.", time: "1 hr ago" },
  { id: 4, type: "receipt", text: "Receipt # RV-B1-AUG26-031 payment confirmed.", time: "2 hr ago" },
  { id: 5, type: "sale",    text: "Sale # BXO-B1-AUG26-989 created by MS. NISHWA.", time: "3 hr ago" },
  { id: 6, type: "warning", text: "Sale # BXO-B1-AUG26-981 payment overdue.", time: "5 hr ago" },
];

const NOTIFICATION_ICONS = {
  warning: <FaExclamationTriangle />,
  sale:    <FaShoppingCart />,
  receipt: <FaReceipt />,
  danger:  <FaTimesCircle />,
};

// ── MatchCircle ────────────────────────────────────────────────────────────
function MatchCircle({ score }) {
  const color = score >= 80 ? "var(--bs-success)"
              : score >= 50 ? "var(--bs-warning)"
              : "var(--bs-danger)";
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: `conic-gradient(${color} ${score * 3.6}deg, #e2e8f0 0)`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%", background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 700, color: "var(--bs-text)",
      }}>
        {score}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function BulkSalesList() {
  const navigate = useNavigate();

  // Data
  const [sales, setSales]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);

  // Filter state
  const [filtersOpen, setFiltersOpen]   = useState(true);
  const range = monthRange();
  const [branch, setBranch]             = useState("");
  const [createdBy, setCreatedBy]       = useState("");
  const [refStaff, setRefStaff]         = useState("");
  const [supplierType, setSupplierType] = useState("");
  const [dateFrom, setDateFrom]         = useState(range.from);
  const [dateTo, setDateTo]             = useState(range.to);
  const [bookingType, setBookingType]   = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [keywordField, setKeywordField] = useState("Sale#");
  const [keyword, setKeyword]           = useState("");
  const [service, setService]           = useState("");
  const [showBalance, setShowBalance]   = useState("No");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // pill filter

  // Pagination
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(10);
  const [showDetailed, setShowDetailed] = useState(false);

  // KPI data (from dashboard or from sales list)
  const [kpi, setKpi] = useState({
    leads: 6, opportunities: 7, salesToday: 0, receiptsToday: 0, receivables: 0
  });

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip:  (page - 1) * perPage,
        limit: perPage,
      });
      if (branch)        params.set("branch", branch);
      if (dateFrom)      params.set("date_from", dateFrom);
      if (dateTo)        params.set("date_to", dateTo);
      if (service)       params.set("service", service);
      if (statusFilter)  params.set("status", statusFilter);
      if (keyword)       { params.set("keyword", keyword); params.set("keyword_field", keywordField); }
      if (paymentStatus) params.set("payment_status", paymentStatus);

      const r    = await fetch(`/api/bulk-sales/?${params}`);
      const data = await r.json();
      setSales(Array.isArray(data) ? data : (data?.data || []));
      setTotal(typeof data?.total === "number" ? data.total : (data?.data?.length || 0));

      // compute KPI from results
      const todayStr = today();
      const todaySales = (Array.isArray(data) ? data : (data?.data || [])).filter(s =>
        (s.value_date || "").startsWith(todayStr) ||
        (s.created_at || "").startsWith(todayStr)
      ).length;
      setKpi(prev => ({ ...prev, salesToday: todaySales }));
    } catch {
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, branch, dateFrom, dateTo, service, statusFilter, keyword, keywordField, paymentStatus]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  // ── Derived values ────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const statusCounts = useMemo(() => {
    const counts = {};
    sales.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
    return counts;
  }, [sales]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bulk sale? This cannot be undone.")) return;
    try {
      await fetch(`/api/bulk-sales/${id}`, { method: "DELETE" });
      fetchSales();
    } catch {}
  };

  const handleDownload = () => {
    const headers = ["Ref#","Value Date","Status","Customer","Sup Total","Sale Total"];
    const rows = sales.map(s => [
      s.ref_number || s.id,
      s.value_date || "",
      s.status,
      s.customer_name || "",
      s.total_purchase_amount,
      s.total_sale_amount,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `bulk_sales_${dateFrom}_${dateTo}.csv`;
    a.click();
  };

  return (
    <div className="bs-container">
      {/* Page Header */}
      <div className="bs-page-header">
        <div>
          <div className="breadcrumb-row">Dashboard &rsaquo; Bulk Sales</div>
          <h1>
            <FaListAlt style={{ marginRight: 10 }} />
            Bulk Sales
            <span className="bs-sub-label" style={{ marginLeft: 14 }}>LIST</span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            id="btn-new-sale-header"
            className="bs-btn bs-btn-accent"
            onClick={() => navigate("/dashboard/bulk-sales/new")}
          >
            <FaPlus /> New Sale
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="bs-kpi-row">
        {[
          { key: "leads",       icon: <FaRocket />,      label: "Leads",              color: "#3b82f6", bar: "leads",      bg: "#eff6ff" },
          { key: "opportunities",icon: <FaClock />,      label: "Pending — Opportunities", color: "#8b5cf6", bar: "opps", bg: "#f5f3ff" },
          { key: "salesToday",  icon: <FaShoppingCart />, label: "Today — Sales",      color: "#0f2d59", bar: "sales",      bg: "#f0f9ff" },
          { key: "receiptsToday",icon: <FaReceipt />,    label: "Today — Receipts",   color: "#0ea5e9", bar: "receipts",   bg: "#e0f2fe" },
          { key: "receivables", icon: <FaCoins />,       label: "Today — Receivables",color: "#10b981", bar: "receivable", bg: "#f0fdf4" },
        ].map(card => (
          <div key={card.key} className="bs-kpi-card">
            <div className={`bs-kpi-bar ${card.bar}`} />
            <div className="bs-kpi-icon" style={{ background: card.bg, color: card.color }}>
              {card.icon}
            </div>
            <div className="bs-kpi-number" style={{ color: card.color }}>
              {kpi[card.key] || 0}
            </div>
            <div className="bs-kpi-label">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Status filter pills */}
      <div className="bs-status-pills-row">
        <div
          className={`bs-status-filter-pill${statusFilter === "" ? " active-pill" : ""}`}
          onClick={() => { setStatusFilter(""); setPage(1); }}
        >All ({total})</div>
        {Object.entries(statusCounts).map(([st, cnt]) => (
          <div
            key={st}
            className={`bs-status-filter-pill${statusFilter === st ? " active-pill" : ""}`}
            onClick={() => { setStatusFilter(st === statusFilter ? "" : st); setPage(1); }}
          >
            {cnt} — {st.charAt(0).toUpperCase() + st.slice(1)}
          </div>
        ))}
      </div>

      {/* Filter Panel */}
      <div className="bs-filter-panel">
        <div className="bs-filter-header" onClick={() => setFiltersOpen(v => !v)}>
          <span><FaFilter size={12} style={{ marginRight: 6 }} />Filters</span>
          {filtersOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </div>
        {filtersOpen && (
          <div className="bs-filter-body">
            {/* Row 1 */}
            <div className="bs-filter-row">
              <div className="bs-filter-field">
                <label>Branch</label>
                <select value={branch} onChange={e => setBranch(e.target.value)}>
                  <option value="">All Branches</option>
                  <option>Rekonic</option>
                  <option>Main Branch</option>
                  <option>Branch 2</option>
                </select>
              </div>
              <div className="bs-filter-field">
                <label>Created By</label>
                <select value={createdBy} onChange={e => setCreatedBy(e.target.value)}>
                  <option value="">All</option>
                </select>
              </div>
              <div className="bs-filter-field">
                <label>Ref Staff</label>
                <select value={refStaff} onChange={e => setRefStaff(e.target.value)}>
                  <option value="">All</option>
                </select>
              </div>
              <div className="bs-filter-field">
                <label>Supplier Type</label>
                <select value={supplierType} onChange={e => setSupplierType(e.target.value)}>
                  <option value="">All</option>
                </select>
              </div>
            </div>
            {/* Row 2 */}
            <div className="bs-filter-row">
              <div className="bs-filter-field">
                <label>Date From</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div className="bs-filter-field">
                <label>Date To</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
              <div className="bs-filter-field">
                <label>Booking Type</label>
                <select value={bookingType} onChange={e => setBookingType(e.target.value)}>
                  <option value="">Choose</option>
                  <option>Online</option>
                  <option>Offline</option>
                </select>
              </div>
              <div className="bs-filter-field" style={{ flex: "2 1 200px" }}>
                <label>Customer</label>
                <input
                  placeholder="Name / mobile / email"
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                />
              </div>
            </div>
            {/* Row 3 */}
            <div className="bs-filter-row">
              <div className="bs-keyword-combo">
                <select value={keywordField} onChange={e => setKeywordField(e.target.value)}>
                  <option>Sale#</option>
                  <option>Ref/LPO#</option>
                  <option>Passenger</option>
                  <option>PNR</option>
                </select>
                <input
                  placeholder="Keyword"
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && fetchSales()}
                />
              </div>
              <div className="bs-filter-field">
                <label>Service</label>
                <select value={service} onChange={e => setService(e.target.value)}>
                  <option value="">All</option>
                  <option>Ticketing</option>
                  <option>Visa</option>
                  <option>Hotel</option>
                  <option>Insurance</option>
                  <option>Baggage</option>
                  <option>Others</option>
                </select>
              </div>
              <div className="bs-filter-field">
                <label>Show Balance</label>
                <select value={showBalance} onChange={e => setShowBalance(e.target.value)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              <div className="bs-filter-field">
                <label>Payment Status</label>
                <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                  <option value="">All</option>
                  <option>Paid</option>
                  <option>Unpaid</option>
                  <option>Partial</option>
                </select>
              </div>
            </div>
            <div className="bs-filter-actions">
              <button
                id="btn-search"
                className="bs-btn bs-btn-primary"
                onClick={() => { setPage(1); fetchSales(); }}
              >
                <FaSearch size={11} /> Search
              </button>
              <button
                className="bs-btn bs-btn-ghost"
                onClick={() => {
                  setBranch(""); setCreatedBy(""); setRefStaff("");
                  setSupplierType(""); setDateFrom(range.from);
                  setDateTo(range.to); setBookingType("");
                  setCustomerSearch(""); setKeyword("");
                  setService(""); setShowBalance("No");
                  setPaymentStatus(""); setStatusFilter("");
                  setPage(1);
                }}
              >
                <FaRedo size={11} /> Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="bs-action-bar">
        <button
          id="btn-new-sale"
          className="bs-btn bs-btn-primary"
          onClick={() => navigate("/dashboard/bulk-sales/new")}
        >
          <FaPlus size={11} /> New Sale
        </button>
        <button className="bs-btn bs-btn-ghost">
          <FaFileImport size={11} /> Import Sale
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <span style={{ fontSize: 12, color: "var(--bs-muted)" }}>Per page:</span>
          <select
            className="bs-form-select"
            style={{ width: 70, height: 30, fontSize: 12 }}
            value={perPage}
            onChange={e => { setPerPage(parseInt(e.target.value)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
          </select>
          <button
            className="bs-btn bs-btn-ghost bs-btn-sm"
            onClick={() => setShowDetailed(v => !v)}
          >
            {showDetailed ? "▲ Hide" : "▼ Show"} Detailed
          </button>
          <button className="bs-btn bs-btn-ghost bs-btn-sm" onClick={handleDownload}>
            <FaDownload size={11} /> Download
          </button>
        </div>
      </div>

      {/* Content: Table + Notifications */}
      <div className="bs-content-row">
        {/* ── Results Table ── */}
        <div className="bs-list-col">
          <div className="bs-table-card">
            <div className="bs-table-top">
              <span className="bs-table-title">
                {loading ? "Loading…" : `${total} record${total !== 1 ? "s" : ""} found`}
              </span>
              <span className="bs-table-meta">
                Page {page} of {totalPages}
              </span>
            </div>

            {loading ? (
              <div className="bs-loading">
                <div className="bs-spinner" />
                <p>Loading bulk sales…</p>
              </div>
            ) : sales.length === 0 ? (
              <div className="bs-empty-state">
                <FaListAlt />
                <p>No bulk sales found matching your filters.</p>
                <button
                  className="bs-btn bs-btn-primary"
                  onClick={() => navigate("/dashboard/bulk-sales/new")}
                >
                  <FaPlus size={11} /> Create First Sale
                </button>
              </div>
            ) : (
              <div className="bs-table-wrapper">
                <table className="bs-table">
                  <thead>
                    <tr>
                      <th>Match</th>
                      <th>Ref #</th>
                      <th>Value Date</th>
                      <th>Created By</th>
                      <th>Service</th>
                      <th>Customer</th>
                      {showDetailed && <th>Supplier</th>}
                      {showDetailed && <th>Billing</th>}
                      <th>Sup Total</th>
                      <th>Sale Total</th>
                      <th style={{ textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map(sale => (
                      <tr key={sale.id}>
                        <td>
                          <MatchCircle score={sale.matching_score || 0} />
                        </td>
                        <td>
                          <div className="bs-ref-badge">
                            <FaCheckCircle size={10} />
                            {sale.ref_number || `BS-${sale.id}`}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--bs-muted)", marginTop: 3 }}>
                            <span className={`bs-status-pill ${sale.status || "draft"}`}>
                              {(sale.status || "draft").toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="muted">{sale.value_date || "—"}</td>
                        <td className="muted">{sale.created_by || "—"}</td>
                        <td>
                          {(sale.line_items || []).slice(0, 2).map((li, i) => (
                            <span key={i} className="bs-service-badge" style={{ marginRight: 4 }}>
                              {li.service_type}
                            </span>
                          ))}
                          {(sale.line_items || []).length > 2 && (
                            <span className="muted" style={{ fontSize: 11 }}>
                              +{sale.line_items.length - 2}
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {sale.customer_name || `Customer #${sale.customer_id}`}
                          </div>
                          {sale.customer_ref_code && (
                            <div style={{ fontSize: 11, color: "var(--bs-muted)" }}>
                              {sale.customer_ref_code}
                            </div>
                          )}
                        </td>
                        {showDetailed && (
                          <td className="muted" style={{ fontSize: 12 }}>
                            {(sale.line_items || []).map(li =>
                              li.supplier_id ? `Supplier #${li.supplier_id}` : null
                            ).filter(Boolean).join(", ") || "—"}
                          </td>
                        )}
                        {showDetailed && (
                          <td className="muted" style={{ fontSize: 12 }}>
                            {sale.billing_full_name || "—"}
                          </td>
                        )}
                        <td style={{ fontWeight: 600 }}>
                          {fmt(sale.total_purchase_amount)}
                        </td>
                        <td style={{ fontWeight: 600, color: "var(--bs-success)" }}>
                          {fmt(sale.total_sale_amount)}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                            <button
                              id={`btn-view-${sale.id}`}
                              className="bs-btn bs-btn-ghost bs-btn-sm bs-btn-icon"
                              title="View / Edit"
                              onClick={() => navigate(`/dashboard/bulk-sales/edit/${sale.id}`)}
                            >
                              <FaEye size={12} />
                            </button>
                            <button
                              id={`btn-delete-${sale.id}`}
                              className="bs-btn bs-btn-danger bs-btn-sm bs-btn-icon"
                              title="Delete"
                              onClick={() => handleDelete(sale.id)}
                            >
                              <FaTrash size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && sales.length > 0 && (
              <div className="bs-pagination">
                <span className="bs-pagination-info">
                  Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
                </span>
                <div className="bs-pagination-btns">
                  <button
                    className="bs-page-btn"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <FaChevronLeft size={11} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return (
                      <button
                        key={pg}
                        className={`bs-page-btn${pg === page ? " active" : ""}`}
                        onClick={() => setPage(pg)}
                      >{pg}</button>
                    );
                  })}
                  <button
                    className="bs-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    <FaChevronRight size={11} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Notifications Panel ── */}
        <div className="bs-notif-col">
          <div className="bs-notif-panel">
            <div className="bs-notif-header">
              <FaBell />
              Notifications
              <span className="bs-notif-badge">{MOCK_NOTIFICATIONS.length}</span>
            </div>
            <div className="bs-notif-list">
              {MOCK_NOTIFICATIONS.map(n => (
                <div key={n.id} className="bs-notif-item">
                  <div className={`bs-notif-icon ${n.type}`}>
                    {NOTIFICATION_ICONS[n.type]}
                  </div>
                  <div>
                    <div className="bs-notif-text">{n.text}</div>
                    <div className="bs-notif-time">{n.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
